import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import { isNone } from "@ember/utils"
import Component from "@glimmer/component"
import type { CardStack } from "@shared/models/card-stack"
import type { Course, Unit } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { handleError } from "cc-frontend/error-handling"
import copyLessonAction from "cc-frontend/lib/actions/lesson/LESSON_COPY"
import type AppState from "cc-frontend/services/app-state"
import type Dialog from "cc-frontend/services/dialog"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type EmberStore from "cc-frontend/services/ember-store"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import type ModalManager from "cc-frontend/services/modal-manager"
import type PlanbookEditor from "cc-frontend/services/planbook-editor"
import type Session from "cc-frontend/services/session"
import { cloneDeep, isNil } from "lodash-es"

interface Args {
  planbook: Planbook
  rotationCalendar: RotationCalendar
  unitCardStack: CardStack
  course: Course
  closeMenu: Function
  modelsNeededForFirstEdit: ModelsNeededForFirstEdit
  unitInfo: Unit
  goToReplaceTemplate: Function
}

export default class UnitMenu extends Component<Args> {
  @service modalManager!: ModalManager
  @service dialog!: Dialog
  @service("ember-store") store!: EmberStore
  @service dispatcher!: Dispatcher
  @service session!: Session
  @service lessonEditor!: LessonEditor
  @service appState!: AppState
  @service planbookEditor!: PlanbookEditor

  @action
  eraseUnit() {
    this.args.closeMenu()
    this.dialog
      .blank("dialogs/confirm-delete-unit", {
        className: "dialog--create-annotation",
      })
      .then((shouldDeleteLessons: boolean) => {
        this.lessonEditor.eraseUnit(
          this.args.unitCardStack.id,
          this.args.course,
          this.args.planbook,
          this.args.rotationCalendar,
          shouldDeleteLessons,
        )
        this.args.closeMenu()
        this.modalManager.closeModal("planner/course/timeline/sidebar")
        this.modalManager.closeModal("planner/modals/unit-comparison-modal")
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }

  @action
  copyUnit() {
    this.args.closeMenu()

    if (this.session.isAuthenticated !== true) {
      this.session.mustSignInToProceed("Copy Lesson")
      return
    }

    if (this.session.userId !== null) {
      this.dispatcher.dispatchPreparedAction(
        copyLessonAction({
          lessonToCopy: this.args.unitCardStack,
          cardStackId: this.args.unitCardStack.id,
          currentUserId: this.session.userId,
          ownerIds: [this.args.planbook.relationships.owner.data.id],
          planbookId: this.args.planbook.id,
          courseId: this.args.course.id,
        }),
      )
    }
  }

  @action
  pasteUnit() {
    if (!isNil(this.session.userId)) {
      let currentUser = this.store.find("user", this.session.userId).then((user) => {
        if (isNil(user?.attributes?.clipboard?.cardStackAttributes)) return
        let newCardStackAttributes = cloneDeep(user.attributes.clipboard.cardStackAttributes)
        if (isNone(newCardStackAttributes)) return
        this.lessonEditor.pasteCardStack(
          this.args.modelsNeededForFirstEdit,
          this.args.unitCardStack.id,
          this.args.course,
          this.args.planbook,
          newCardStackAttributes,
          this.args.unitCardStack.attributes,
          "unit",
          newCardStackAttributes.parentCardStack,
        )
      })
    }
    this.args.closeMenu()
  }

  @action
  viewVersionHistory() {
    this.modalManager.openModal("planner/modals/lesson-history", {
      cardStack: this.args.unitCardStack,
    })
    this.args.closeMenu()
  }

  @action
  printOrDownload() {
    this.modalManager.openModal("planner/modals/print-download", {
      courseId: this.args.course.id,
      planbookId: this.args.planbook.id,
      unitId: this.args.unitCardStack.id,
      startDate: this.args.unitInfo.startDate,
      endDate: this.args.unitInfo.endDate,
      layout: "course-unit",
      canPrint: true,
    })

    this.args.closeMenu()
  }
}
